import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { Tag } from "@/components/ui/tag"
import { HealthDataCardSession } from "@/modules/patients/detail/health-information"
import { FamilyHistoryDrawer } from "@/modules/patients/detail/health-information/family-history/family-history-drawer"
import { useGetHealthInformationVerification } from "@/modules/patients/detail/health-information/hooks/useGetHealthInformationVerification"
import { useHealthInformationInContextAppointment } from "@/modules/patients/detail/health-information/hooks/useHealthInformationCanVerify"
import { FamilyHistoryFragment$key } from "@/modules/patients/services/__generated__/FamilyHistoryFragment.graphql"
import { Country } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { FamilyHistoryFragment } from "@/modules/patients/services/FamilyHistoryFragment"
import { usePatientFamilyHistory } from "@/modules/patients/services/usePatientFamilyHistory"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface FamilyHistoryBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  country: Country
  patientId: string
}

const FamilyHistory = ({ country, patientId }: FamilyHistoryBodyProps) => {
  const dataRef = usePatientFamilyHistory(patientId, country)
  const { comment, heartDisease, diabetes, cancer, autoimmuneDisease } = useFragment<FamilyHistoryFragment$key>(
    FamilyHistoryFragment,
    dataRef,
  )
  const { t } = useTranslation()
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const drawerName = useDrawerStore((state) => state.drawerName)
  const { verifiedBy, verifiedAt, isVerified } = useGetHealthInformationVerification(
    patientId,
    country,
    "FAMILY_HISTORY",
  )

  const validAppointment = useHealthInformationInContextAppointment()
  const canVerify = !isVerified && validAppointment

  const renderDiabetesTag = () => {
    switch (diabetes) {
      case "YES_DIABETES_TYPE_ONE":
        return <Tag type="neutral" label={t("patient.healthInformation.familyHistory.diabetes.typeOne")} />
      case "YES_DIABETES_TYPE_ONE_AND_TWO":
        return (
          <Tag
            data-testid="family-history-Diabetes-label"
            type="neutral"
            label={t("patient.healthInformation.familyHistory.diabetes.typeOneAndTwo")}
          />
        )
      case "YES_DIABETES_TYPE_TWO":
        return (
          <Tag
            data-testid="family-history-Diabetes-label"
            type="neutral"
            label={t("patient.healthInformation.familyHistory.diabetes.typeTwo")}
          />
        )
      case "NO":
      case "NOT_SPECIFIED":
      case "UNKNOWN":
        return undefined
    }
  }

  const showTag = () => {
    if (heartDisease !== "YES" && cancer !== "YES" && autoimmuneDisease !== "YES" && !renderDiabetesTag() && !comment) {
      return false
    }
    return true
  }

  return (
    <HealthDataCardSession
      data-testid="family-history-title"
      canVerify={canVerify}
      sectionName="family-history"
      analyticsSectionName="healthInformation_family"
      disabled={drawerName === "FamilyHistoryDrawer"}
      title={t("patient.healthInformation.familyHistory.title")}
      editHandler={() => {
        openDrawer(
          () => (
            <FamilyHistoryDrawer canVerify={canVerify} verifiedBy={verifiedBy ?? ""} verifiedAt={verifiedAt ?? ""} />
          ),
          "FamilyHistoryDrawer",
        )
      }}>
      {showTag() && (
        <div className="grid gap-2">
          <span
            data-testid="family-history-comment"
            className="text-on-surface-subdued text-sm leading-5 font-normal break-all">
            {comment}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {heartDisease === "YES" && (
              <Tag
                data-testid="family-history-heart-disease-label"
                type="neutral"
                label={t("patient.healthInformation.familyHistory.heartDisease.label")}
              />
            )}

            {renderDiabetesTag()}

            {cancer === "YES" && (
              <Tag
                data-testid="family-history-cancer-label"
                type="neutral"
                label={t("patient.healthInformation.familyHistory.cancer.label")}
              />
            )}

            {autoimmuneDisease === "YES" && (
              <Tag
                data-testid="family-history-autoimmune-disease-label"
                type="neutral"
                label={t("patient.healthInformation.familyHistory.autoimmuneDisease.label")}
              />
            )}
          </div>
        </div>
      )}
    </HealthDataCardSession>
  )
}

export { FamilyHistory }
